<template>
  <b-container>
    <b-modal v-model="formEditVisible" :title="editorTitle" @ok="onModalOk" :ok-disabled="aiSubmitting" :ok-title="activeTab === 4 ? (aiSubmitting ? 'updating...' : 'update with AI') : 'OK'" size="xl" modal-class="view-editor-modal">
      <template #title>
        {{ editorTitle }}
        <modal-help topic="edit-form" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>

          <b-tab title="General">
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="form unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="form title" required />
                </label>
              </div>
            </div>
            <div v-if="showAuthenticatorOption" class="tab-section">
              <div class="section-heading">Authenticator</div>
              <label class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="twoFactor" />
                {{ authenticatorLabel }}
              </label>
              <p class="section-desc">{{ authenticatorNote }}</p>
            </div>
            <div v-if="pageRecordLink" class="tab-section">
              <div class="section-heading">Page record</div>
              <label class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="filterByPageRecord" />
                relate each new record to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. a new record is linked through {{ pageRecordLink.columnTitle }}.</p>
            </div>
          </b-tab>

          <b-tab title="Layout">
            <div class="tab-section" v-if="form.components">
              <div v-for="(component, componentIndex) in form.components" :key="componentIndex" class="form-row-block">
                <div class="form-row-header">
                  <span class="form-row-label">row {{componentIndex + 1}}</span>
                  <span @click="removeComponent(form, component)" class="delete-inline">x</span>
                </div>
                <div class="form-row-columns">
                  <div v-for="(subComponent, subComponentIndex) in component.components" :key="subComponentIndex" class="form-col-block">
                    <div class="form-edit-header">
                      <span class="form-col-label">column {{subComponentIndex + 1}}</span>
                      <span @click="removeComponent(component, subComponent)" class="delete-inline">x</span>
                    </div>
                    <div v-for="(subSubComponent, subSubComponentIndex) in subComponent.components" :key="subSubComponentIndex" class="form-edit-item">
                      <div class="editable-component" @click="editComponent(subComponent, subSubComponent)">
                        <div class="component-type">{{subSubComponent.type}}</div>
                        <div class="component-details" v-if="subSubComponent.uniqueTitle || subSubComponent.title || subSubComponent.description">
                          <span v-if="subSubComponent.uniqueTitle" class="component-detail"><span class="detail-label">field:</span> {{subSubComponent.uniqueTitle}}<span v-if="getColumnType(subSubComponent.uniqueTitle)" class="column-type-badge">{{getColumnType(subSubComponent.uniqueTitle)}}</span></span>
                          <span v-if="subSubComponent.title" class="component-detail"><span class="detail-label">title:</span> {{subSubComponent.title}}</span>
                          <span v-if="subSubComponent.description" class="component-detail"><span class="detail-label">description:</span> {{subSubComponent.description}}</span>
                        </div>
                      </div>
                      <span
                        @click.stop="openRuleEditor(subSubComponent)"
                        class="rules-btn"
                        :class="{ 'has-rules': (subSubComponent.formattingRules && subSubComponent.formattingRules.rules && subSubComponent.formattingRules.rules.length) || (subSubComponent.valueFormatting && subSubComponent.valueFormatting.type) }"
                        title="Formatting rules - add conditional styling based on field values"
                      ><span class="rules-btn-inner">&#9889;<span class="rules-btn-label">rules</span></span></span>
                      <span @click="removeComponent(subComponent, subSubComponent)" class="delete">x</span>
                    </div>
                    <div @click="createComponent(subComponent)" class="add-btn">
                      + add component
                    </div>
                  </div>
                </div>
                <div @click="addColumn(component)" class="add-btn" style="margin-left: 12px;">
                  + add column
                </div>
              </div>
              <div
                v-if="showAddAllColumns"
                @click="addAllSpreadsheetColumns"
                class="add-btn add-all-columns-btn"
              >
                + add all columns from {{ spreadsheetTitle }}
              </div>
              <div @click="addRow(form)" class="add-btn">
                + add row
              </div>
            </div>
          </b-tab>

          <b-tab title="Actions">
            <div class="tab-section">
              <div class="section-heading">Form Actions</div>
              <div class="section-description">Run an automation or a step workflow when this form is submitted.</div>
              <div v-for="(action, actionIndex) in formActions" :key="'fa' + actionIndex" class="form-action-block">
                <div class="form-action-header">
                  <span class="form-action-label">Action {{ actionIndex + 1 }}</span>
                  <span @click="removeFormAction(actionIndex)" class="delete-inline">x</span>
                </div>
                <div class="field-row">
                  <label class="field-label">
                    runs
                    <b-form-select
                      :model-value="formActionKind(action)"
                      :options="formActionKindOptions"
                      size="sm"
                      @update:model-value="onFormActionKindChange(action, $event)"
                    />
                  </label>
                </div>
                <template v-if="formActionKind(action) === 'step_workflow'">
                  <div class="field-row">
                    <label class="field-label">
                      workflow
                      <b-form-select
                        v-model="action.stepWorkflowUniqueTitle"
                        :options="stepWorkflowOptions"
                        size="sm"
                      />
                    </label>
                    <p v-if="workflowWarning(action.stepWorkflowUniqueTitle)" class="workflow-overlap-warning">
                      {{ workflowWarning(action.stepWorkflowUniqueTitle) }}
                    </p>
                  </div>
                  <div class="field-row">
                    <label class="field-label">
                      starting record
                      <b-form-select
                        v-model="action.recordSource"
                        :options="workflowRecordOptions"
                        size="sm"
                      />
                    </label>
                  </div>
                  <div v-if="action.recordSource === 'related'" class="field-row">
                    <label class="field-label">
                      relationship
                      <b-form-select
                        :model-value="workflowRelationshipKey(action)"
                        :options="workflowRelationshipOptions"
                        size="sm"
                        @update:model-value="setWorkflowRelationship(action, $event)"
                      />
                    </label>
                  </div>
                </template>
                <template v-else>
                <div class="field-row">
                  <label class="field-label">
                    automation
                    <b-form-select
                      v-model="action.automationUniqueTitle"
                      :options="availableAutomationOptions"
                      size="sm"
                      @change="onAutomationSelected(action)"
                    />
                  </label>
                </div>
                <div v-if="action.automationVariables && action.automationVariables.length" class="variable-mappings">
                  <div class="variable-mappings-header">Variable Mappings</div>
                  <div class="kv-row kv-header-row">
                    <div class="kv-variable-name">Variable</div>
                    <div class="kv-variable-source">Source</div>
                    <div class="kv-variable-field">Field</div>
                  </div>
                  <div v-for="(variable, varIndex) in action.automationVariables" :key="'v' + varIndex" class="kv-row">
                    <div class="kv-variable-name">
                      <span class="variable-tag">{{ variable.variableName }}</span>
                      <span class="variable-location">{{ locationLabel(variable.location) }}</span>
                    </div>
                    <div class="kv-variable-source">
                      <b-form-select
                        v-model="variable.source"
                        :options="variableSourceOptions"
                        size="sm"
                        class="kv-input"
                        @change="onVariableSourceChange(action, variable)"
                      />
                    </div>
                    <div class="kv-variable-field">
                      <b-form-select
                        v-if="variable.source === 'field'"
                        v-model="variable.fieldUniqueTitle"
                        :options="spreadSheetColumnOptions"
                        size="sm"
                        class="kv-input"
                      />
                      <b-form-select
                        v-if="variable.source === 'related'"
                        v-model="variable.relatedSpreadsheet"
                        :options="relatedSpreadsheetOptions(action)"
                        size="sm"
                        class="kv-input"
                        placeholder="relationship"
                        @change="onRelatedSpreadsheetChange(action, variable)"
                      />
                      <b-form-select
                        v-if="variable.source === 'related' && variable.relatedSpreadsheet"
                        v-model="variable.relatedField"
                        :options="relatedFieldOptions(variable)"
                        size="sm"
                        class="kv-input"
                        style="margin-top: 4px;"
                      />
                      <b-form-input
                        v-if="variable.source === 'static'"
                        v-model="variable.staticValue"
                        placeholder="static value"
                        size="sm"
                        class="kv-input"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="action.automationUniqueTitle && (!action.automationVariables || action.automationVariables.length === 0)" class="no-variables-msg">
                  This automation has no named variables. Name one on the automation, such as a query param, an email field, or a column set from a variable.
                </div>
                </template>
              </div>
              <div @click="addFormAction" class="add-btn">+ add action</div>
            </div>
          </b-tab>

          <b-tab title="Rules">
            <div class="tab-section">
              <div class="section-heading">Automation</div>
              <div class="rules-button-group">
                <button
                  @click="defaultValueRuleEditorVisible = true"
                  class="submission-rules-btn"
                  :class="{ 'has-rules': defaultValueRules && defaultValueRules.length }"
                >
                  &#9889; Default value rules
                  <span v-if="defaultValueRules && defaultValueRules.length" class="submission-rules-count">{{ defaultValueRules.length }}</span>
                </button>
                <button
                  @click="submissionRuleEditorVisible = true"
                  class="submission-rules-btn"
                  :class="{ 'has-rules': submissionRules && submissionRules.length }"
                >
                  &#9889; Submission rules
                  <span v-if="submissionRules && submissionRules.length" class="submission-rules-count">{{ submissionRules.length }}</span>
                </button>
              </div>
            </div>
          </b-tab>

          <b-tab title="AI">
            <ViewAiPanel v-model="aiPrompt" :error="aiError || ''" :submitting="aiSubmitting" />
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
    <selectColumn ref="selectColumnRef"></selectColumn>
    <RuleEditor
      :visible="ruleEditorVisible"
      :rules="ruleEditorRules"
      :value-formatting="ruleEditorValueFormatting"
      :spreadsheet="spreadsheet"
      :all-spreadsheets="allSpreadsheets"
      :column-unique-title="ruleEditorColumnUniqueTitle"
      :column-data-type="ruleEditorColumnDataType"
      :application-unique-title="applicationUniqueTitle"
      @update:visible="ruleEditorVisible = $event"
      @update:rules="onRulesUpdate"
      @update:valueFormatting="onValueFormattingUpdate"
    />
    <DefaultValueRuleEditor
      :visible="defaultValueRuleEditorVisible"
      :rules="defaultValueRules"
      :spreadsheet="spreadsheet"
      :all-spreadsheets="allSpreadsheets"
      :application-unique-title="applicationUniqueTitle"
      :pages="pages"
      :current-page-unique-title="currentPageUniqueTitle"
      @update:visible="defaultValueRuleEditorVisible = $event"
      @update:rules="onDefaultValueRulesUpdate"
    />
    <SubmissionRuleEditor
      :visible="submissionRuleEditorVisible"
      :rules="submissionRules"
      :spreadsheet="spreadsheet"
      :all-spreadsheets="allSpreadsheets"
      :pages="pages"
      :page-components="pageComponents"
      :current-page-unique-title="currentPageUniqueTitle"
      :application-unique-title="applicationUniqueTitle"
      @update:visible="submissionRuleEditorVisible = $event"
      @update:rules="onSubmissionRulesUpdate"
    />
  </b-container>
</template>

<script>

  import axios from 'axios'
  import selectColumn from './selectcolumn.vue'
  import RuleEditor from './lib/RuleEditor.vue'
  import SubmissionRuleEditor from './lib/SubmissionRuleEditor.vue'
  import DefaultValueRuleEditor from './lib/DefaultValueRuleEditor.vue'
  import { inferFormulaResultType } from '../lib/formula-utils.js'
  import { formControlForColumn } from '../../../shared/column-kind.js'
  import { durationUnit } from '../../../shared/duration-column.js'
  import { buildComponentsFromSpreadsheetColumns } from '../lib/spreadsheet-view-fields.js'
  import { pageRecordRelationship } from '../lib/page-record-link.js'
import { relationshipWriteTargets } from '../../../shared/submission-record-actions.js'
  import { viewEditorTabAfterColumnDialog } from '../lib/view-editor-tab.js'
  import { submitViewAi } from '../lib/view-ai.js'
  import ViewAiPanel from './ViewAiPanel.vue'
  import { automationVariableNames, variableLocationLabel } from '../../../shared/automation-integration.js'

  export default {
    name: `formEdit`,
    data () {

      return {
        formEditVisible: false,
        activeTab: 0,
        aiPrompt: ``,
        aiError: ``,
        aiSubmitting: false,
        aiSavedUniqueTitle: ``,
        title: ``,
        uniqueTitle: ``,
        spreadSheetColumnOptions: [],
        spreadSheetUniqueTitle: ``,
        spreadsheet: null,
        form: {
          components: []
        },
        parentDesigner: null,
        allSpreadsheets: [],
        ruleEditorVisible: false,
        ruleEditorRules: [],
        ruleEditorColumnUniqueTitle: '',
        ruleEditorColumnDataType: 'string',
        ruleEditorValueFormatting: null,
        ruleEditorComponent: null,
        submissionRuleEditorVisible: false,
        submissionRules: [],
        pages: [],
        pageComponents: [],
        currentPageUniqueTitle: '',
        defaultValueRuleEditorVisible: false,
        defaultValueRules: [],
        applicationUniqueTitle: '',
        filterByPageRecord: false,
        componentType: 'form',
        twoFactor: false,
        pageRecordSpreadSheetUniqueTitle: '',
        formActions: [],
        availableAutomations: [],
        availableWorkflows: [],
        workflowOverlaps: {},
        relatedSpreadsheetColumnsCache: {}
      }
    },
    components: {
      selectColumn,
      RuleEditor,
      SubmissionRuleEditor,
      DefaultValueRuleEditor,
      ViewAiPanel
    },
    computed: {
      editorTitle () {

        if (this.componentType === 'login') return 'edit a login'

        if (this.componentType === 'registration') return 'edit a registration form'

        return 'edit a form'
      },
      showAuthenticatorOption () {

        return this.componentType === 'login' || this.componentType === 'registration'
      },
      authenticatorLabel () {

        if (this.componentType === 'registration') return 'set up an authenticator after the username and password'

        return 'require an authenticator app'
      },
      authenticatorNote () {

        if (this.componentType === 'registration') {

          return 'The account is created after the person scans the code and enters the 6-digit code. Leave this off to create the account from the username and password only. The app authenticator policy can turn this off for every view, or require it even when this box is clear.'
        }

        return 'The first sign-in sets up an authenticator app. Later sign-ins ask for a code from that app, or a recovery code. Someone who already set one up is asked for a code on every login form. Users settings can turn that off, or require it on a login view that does not have this checked.'
      },
      spreadsheetTitle () {
        return (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'spreadsheet'
      },
      pageRecordLink () {
        return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
      },
      showAddAllColumns () {
        if (this.form.components && this.form.components.length) return false
        return buildComponentsFromSpreadsheetColumns(this.spreadsheet && this.spreadsheet.columns, 'form').length > 0
      },
      availableAutomationOptions () {

        const options = [{ value: '', text: '-- select an automation --' }]

        for (const automation of this.availableAutomations) {
          options.push({ value: automation.uniqueTitle, text: automation.title || automation.uniqueTitle })
        }

        return options
      },
      formActionKindOptions () {
        return [
          { value: 'automation', text: 'Automation' },
          { value: 'step_workflow', text: 'Step workflow' }
        ]
      },
      stepWorkflowOptions () {
        const options = [{ value: '', text: '-- select a workflow --' }]
        for (const workflow of this.availableWorkflows) {
          options.push({ value: workflow.uniqueTitle, text: workflow.title || workflow.uniqueTitle })
        }
        return options
      },
      workflowRecordOptions () {
        return [
          { value: 'form', text: 'This form\'s record' },
          { value: 'related', text: 'A related record' }
        ]
      },
      workflowRelationshipOptions () {
        const options = [{ value: '', text: 'Relationship' }]
        for (const target of relationshipWriteTargets(this.spreadsheet, this.allSpreadsheets)) {
          options.push({ value: target.key, text: target.label })
        }
        return options
      },
      variableSourceOptions () {

        return [
          { value: 'field', text: 'Spreadsheet Field' },
          { value: 'related', text: 'Related Field' },
          { value: 'static', text: 'Static Value' }
        ]
      }
    },
    methods: {
      locationLabel (location) {

        return variableLocationLabel(location)
      },
      getColumnType (uniqueTitle) {
        if (!uniqueTitle) return null
        const systemTypes = { id: 'number', createdAt: 'dateTime', updatedAt: 'dateTime' }
        if (systemTypes[uniqueTitle]) return systemTypes[uniqueTitle]
        if (!this.spreadsheet || !this.spreadsheet.columns) return null
        const col = this.spreadsheet.columns.find(c => c.uniqueTitle === uniqueTitle)
        return col ? col.type : null
      },
      addColumn (component) {

        console.log(`add column!`)

        component.components.push({
          type: `formGroup`,
          components: []
        })
      },
      addAllSpreadsheetColumns () {
        if (!this.showAddAllColumns) return
        const rows = buildComponentsFromSpreadsheetColumns(this.spreadsheet.columns, 'form')
        if (!this.form.components) this.form.components = []
        this.form.components.push(...rows)
      },
      addRow (component) {

        console.log(`add row!`)

        component.components.push({
          type: `formRow`,
          components: [{
            type: `formGroup`,
            components: []
          }]
        })
      },
      removeComponent (parentComponent, removeComponent) {

        console.log(`components before!`)
        console.log(JSON.stringify(parentComponent.components))

        parentComponent.components = _.filter(parentComponent.components, (component) => {

          return component !== removeComponent
        })

        console.log(`components after!`)
        console.log(JSON.stringify(parentComponent.components))
      },
      async getSpreadSheet () {

        console.log(`getSpreadSheet! ${this.spreadSheetUniqueTitle}`)

        const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })

        console.log(`have spreadsheets!`)
        console.log(spreadsheet)

        this.spreadsheet = spreadsheet.data

        if (!this.spreadsheet.columns) {

          return
        }

        const systemFields = [
          { value: 'id', text: 'ID (system)' },
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' }
        ]

        this.spreadSheetColumnOptions = [
          ...systemFields,
          ...this.spreadsheet.columns.map((column) => {
            return {
              value: column.uniqueTitle,
              text: column.title,
              type: column.type,
              selectionMode: column.selectionMode || `multiple`
            }
          })
        ]
      },
      async showEditFormComponent (components, spreadSheetUniqueTitle, options) {

        console.log('EVENT FIRED!!!!!!')
        console.log(components)

        this.activeTab = viewEditorTabAfterColumnDialog(this.formEditVisible, this)
        this.aiPrompt = ''
        this.aiError = ''
        this.aiSubmitting = false
        this.aiSavedUniqueTitle = this.uniqueTitle
        this.form.components = components
        this.spreadSheetUniqueTitle = spreadSheetUniqueTitle
        this.componentType = (options && options.componentType) || 'form'
        this.twoFactor = !!(options && (options.twoFactor === true || options.twoFactor === 'true'))

        if (options) {
          this.submissionRules = (options.submissionRules && options.submissionRules.rules) ? options.submissionRules.rules : []
          this.defaultValueRules = (options.defaultValueRules && options.defaultValueRules.rules) ? options.defaultValueRules.rules : []
          this.formActions = ((options.formActions && options.formActions.actions) ? options.formActions.actions : []).map((action) => this.normalizeFormAction(action))
          this.pages = options.pages || []
          this.pageComponents = options.pageComponents || []
          this.currentPageUniqueTitle = options.currentPageUniqueTitle || ''
        }

        await this.getSpreadSheet()
        await this.fetchAvailableAutomations()
        await this.fetchAvailableWorkflows()
        await this.fetchWorkflowOverlaps()

        console.log(`have spreadsheet column options`)
        console.log(this.spreadSheetColumnOptions)

        this.formEditVisible = true
      },
      async createComponent (component) {

        this.activeSlot = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showSelectColumnModal()
      },
      async editComponent (parentComponent, component) {

        this.activeSlot = parentComponent
        this.editingComponent = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showEditColumnModal(component)
      },
      editComponentInActiveSlot (selectedColumnUniqueTitle, title, description, placeholder, multiselectConfig) {

        const systemColumns = { id: { type: 'number' }, createdAt: { type: 'dateTime' }, updatedAt: { type: 'dateTime' } }
        const selectedColumn = systemColumns[selectedColumnUniqueTitle] || _.find(this.spreadsheet.columns, (column) => {

          return column.uniqueTitle === selectedColumnUniqueTitle
        })

        const type = formControlForColumn(selectedColumn)

        if (!type) return

        this.editingComponent.type = type
        this.editingComponent.uniqueTitle = `${selectedColumnUniqueTitle}`
        this.editingComponent.columnType = selectedColumn.type
        this.editingComponent.title = title || ``
        this.editingComponent.description = description || ``
        this.editingComponent.placeholder = placeholder || ``
        if (selectedColumn.type === 'image') this.editingComponent.allowImageUrl = true
        if (selectedColumn.type === 'duration') this.editingComponent.durationUnit = durationUnit(selectedColumn)
        if (selectedColumn.type === 'dateTime') this.editingComponent.dateOnly = !!selectedColumn.dateOnly
        this.applyMultiselectConfig(this.editingComponent, multiselectConfig)
      },
      async addComponentToActiveSlot (selectedColumnUniqueTitle, title, description, placeholder, multiselectConfig) {

        console.log(`add form input for~~`)
        console.log(selectedColumnUniqueTitle)

        const systemColumns = { id: { type: 'number' }, createdAt: { type: 'dateTime' }, updatedAt: { type: 'dateTime' } }
        const selectedColumn = systemColumns[selectedColumnUniqueTitle] || _.find(this.spreadsheet.columns, (column) => {

          return column.uniqueTitle === selectedColumnUniqueTitle
        })

        const type = formControlForColumn(selectedColumn)

        if (!type) return

        const component = {
          type: type, // todo: could be text area, select, etc
          uniqueTitle: `${selectedColumnUniqueTitle}`,
          columnType: selectedColumn.type,
          title: title || ``,
          description: description || ``,
          placeholder: placeholder || ``
        }

        if (selectedColumn.type === 'image') component.allowImageUrl = true
        if (selectedColumn.type === 'duration') component.durationUnit = durationUnit(selectedColumn)
        if (selectedColumn.type === 'dateTime') component.dateOnly = !!selectedColumn.dateOnly

        this.applyMultiselectConfig(component, multiselectConfig)
        this.activeSlot.components.push(component)
      },
      applyMultiselectConfig (component, multiselectConfig) {

        if (component.type !== `formMultiSelect`) {

          delete component.choices
          delete component.displayStyle
          delete component.selectionMode

          return
        }

        const column = _.find(this.spreadsheet && this.spreadsheet.columns, (item) => item.uniqueTitle === component.uniqueTitle)
        component.choices = column && Array.isArray(column.choices) ? column.choices.slice() : []
        component.displayStyle = (multiselectConfig && multiselectConfig.displayStyle) || `dropdown`
        component.selectionMode = (multiselectConfig && multiselectConfig.selectionMode) || `multiple`
      },
      openRuleEditor (component) {

        this.ruleEditorComponent = component
        this.ruleEditorColumnUniqueTitle = component.uniqueTitle || ''
        this.ruleEditorRules = (component.formattingRules && component.formattingRules.rules) ? component.formattingRules.rules : []
        this.ruleEditorValueFormatting = component.valueFormatting || null

        let dataType = 'string'
        if (this.spreadsheet && this.spreadsheet.columns) {
          const col = this.spreadsheet.columns.find(c => c.uniqueTitle === component.uniqueTitle)
          if (col) {
            if (col.type === 'formula') {
              dataType = inferFormulaResultType(col.formula)
            } else {
              const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', password: 'string', richText: 'string', relationship: 'relationship', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
              dataType = typeMap[col.type] || 'string'
            }
          }
        }
        this.ruleEditorColumnDataType = dataType

        this.ruleEditorVisible = true
      },
      onRulesUpdate (rules) {

        if (this.ruleEditorComponent) {
          this.ruleEditorComponent.formattingRules = { rules }
        }
      },
      onValueFormattingUpdate (valueFormatting) {

        if (this.ruleEditorComponent) {
          this.ruleEditorComponent.valueFormatting = valueFormatting
        }
      },
      onSubmissionRulesUpdate (rules) {

        this.submissionRules = rules
      },
      onDefaultValueRulesUpdate (rules) {

        this.defaultValueRules = rules
      },

      // Form Actions methods
      async fetchAvailableAutomations () {

        if (!this.applicationUniqueTitle) return

        try {

          const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/automations/`, {
            headers: { 'application-unique-title': this.applicationUniqueTitle }
          })

          this.availableAutomations = response.data
        } catch (error) {

          console.log('error fetching automations:', error)
          this.availableAutomations = []
        }
      },
      async fetchAvailableWorkflows () {
        if (!this.applicationUniqueTitle) return
        try {
          const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, {
            headers: { 'application-unique-title': this.applicationUniqueTitle }
          })
          this.availableWorkflows = response.data || []
        } catch (error) {
          this.availableWorkflows = []
        }
      },
      async fetchWorkflowOverlaps () {
        if (!this.applicationUniqueTitle || !this.spreadSheetUniqueTitle) {
          this.workflowOverlaps = {}
          return
        }
        try {
          const response = await this.$http.get(
            `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}/workflow-overlaps`,
            { headers: { 'application-unique-title': this.applicationUniqueTitle } }
          )
          this.workflowOverlaps = response.data || {}
        } catch (error) {
          this.workflowOverlaps = {}
        }
      },
      workflowWarning (workflowUniqueTitle) {
        const entry = workflowUniqueTitle && this.workflowOverlaps[workflowUniqueTitle]
        return entry && entry.formWarning ? entry.formWarning : ''
      },
      formActionKind (action) {
        if (!action) return 'automation'
        if (action.kind === 'step_workflow' || (action.stepWorkflowUniqueTitle && !action.automationUniqueTitle)) return 'step_workflow'
        return 'automation'
      },
      normalizeFormAction (action) {
        const next = Object.assign({ kind: 'automation', recordSource: 'form' }, action || {})
        next.kind = this.formActionKind(next)
        return next
      },
      formActionForSave (action) {
        if (this.formActionKind(action) === 'step_workflow') {
          return {
            kind: 'step_workflow',
            stepWorkflowUniqueTitle: action.stepWorkflowUniqueTitle || '',
            recordSource: action.recordSource === 'related' ? 'related' : 'form',
            relatedSource: action.relatedSource || 'forward',
            relationshipColumn: action.relationshipColumn || '',
            sourceSpreadsheet: action.sourceSpreadsheet || '',
            sourceRelationshipColumn: action.sourceRelationshipColumn || '',
            relatedSpreadSheet: action.relatedSpreadSheet || ''
          }
        }
        return {
          automationUniqueTitle: action.automationUniqueTitle || '',
          automationVariables: action.automationVariables || []
        }
      },
      onFormActionKindChange (action, kind) {
        action.kind = kind
        if (kind === 'step_workflow') {
          action.automationUniqueTitle = ''
          action.automationVariables = []
          action.recordSource = action.recordSource || 'form'
          return
        }
        action.stepWorkflowUniqueTitle = ''
        action.recordSource = 'form'
      },
      workflowRelationshipKey (action) {
        if (!action) return ''
        if (action.relatedSource === 'reverse' && action.sourceSpreadsheet && action.sourceRelationshipColumn) {
          return `reverse:${action.sourceSpreadsheet}:${action.sourceRelationshipColumn}`
        }
        if (action.relationshipColumn) return `forward:${action.relationshipColumn}`
        return ''
      },
      setWorkflowRelationship (action, key) {
        const target = relationshipWriteTargets(this.spreadsheet, this.allSpreadsheets).find((item) => item.key === key)
        if (!target) return
        action.relatedSource = target.relatedSource
        action.relationshipColumn = target.relationshipColumn || ''
        action.sourceSpreadsheet = target.sourceSpreadsheet || ''
        action.sourceRelationshipColumn = target.sourceRelationshipColumn || ''
        action.relatedSpreadSheet = target.relatedSpreadSheet || ''
      },
      addFormAction () {

        this.formActions.push({
          kind: 'automation',
          automationUniqueTitle: '',
          automationVariables: [],
          stepWorkflowUniqueTitle: '',
          recordSource: 'form'
        })
      },
      removeFormAction (index) {

        this.formActions.splice(index, 1)
      },
      async onAutomationSelected (action) {

        if (!action.automationUniqueTitle) {
          action.automationVariables = []
          return
        }

        try {

          const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/automations/${action.automationUniqueTitle}`, {
            headers: { 'application-unique-title': this.applicationUniqueTitle }
          })

          const automation = response.data
          const variables = automationVariableNames(automation).map((item) => ({
            variableName: item.variableName,
            location: item.location,
            source: 'field',
            fieldUniqueTitle: '',
            relatedSpreadsheet: '',
            relatedField: '',
            staticValue: ''
          }))

          // Preserve existing mappings if variable names match
          const existingMappings = {}
          if (action.automationVariables) {
            for (const v of action.automationVariables) {
              existingMappings[v.variableName] = v
            }
          }

          for (const v of variables) {
            if (existingMappings[v.variableName]) {
              v.source = existingMappings[v.variableName].source
              v.fieldUniqueTitle = existingMappings[v.variableName].fieldUniqueTitle
              v.relatedSpreadsheet = existingMappings[v.variableName].relatedSpreadsheet
              v.relatedField = existingMappings[v.variableName].relatedField
              v.staticValue = existingMappings[v.variableName].staticValue
            }
          }

          action.automationVariables = variables
        } catch (error) {

          console.log('error fetching automation config:', error)
          action.automationVariables = []
        }
      },
      onVariableSourceChange (action, variable) {

        variable.fieldUniqueTitle = ''
        variable.relatedSpreadsheet = ''
        variable.relatedField = ''
        variable.staticValue = ''
      },
      relatedSpreadsheetOptions (action) {

        if (!this.spreadsheet || !this.spreadsheet.columns) return [{ value: '', text: '-- select relationship --' }]

        const options = [{ value: '', text: '-- select relationship --' }]

        for (const col of this.spreadsheet.columns) {
          if (col.type === 'relationship' && col.relatedSpreadSheet) {
            options.push({ value: col.uniqueTitle, text: col.title || col.uniqueTitle })
          }
        }

        return options
      },
      async onRelatedSpreadsheetChange (action, variable) {

        variable.relatedField = ''

        if (!variable.relatedSpreadsheet) return

        // Find the related spreadsheet unique title from the relationship column
        const relCol = this.spreadsheet.columns.find(c => c.uniqueTitle === variable.relatedSpreadsheet)
        if (!relCol || !relCol.relatedSpreadSheet) return

        // Cache the related spreadsheet columns
        if (!this.relatedSpreadsheetColumnsCache[relCol.relatedSpreadSheet]) {

          try {

            const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${relCol.relatedSpreadSheet}`, {
              headers: { 'application-unique-title': this.applicationUniqueTitle }
            })
            this.relatedSpreadsheetColumnsCache[relCol.relatedSpreadSheet] = response.data.columns || []
          } catch (error) {

            console.log('error fetching related spreadsheet:', error)
            this.relatedSpreadsheetColumnsCache[relCol.relatedSpreadSheet] = []
          }
        }
      },
      relatedFieldOptions (variable) {

        if (!variable.relatedSpreadsheet || !this.spreadsheet || !this.spreadsheet.columns) {
          return [{ value: '', text: '-- select field --' }]
        }

        const relCol = this.spreadsheet.columns.find(c => c.uniqueTitle === variable.relatedSpreadsheet)
        if (!relCol || !relCol.relatedSpreadSheet) return [{ value: '', text: '-- select field --' }]

        const cachedColumns = this.relatedSpreadsheetColumnsCache[relCol.relatedSpreadSheet]
        if (!cachedColumns) return [{ value: '', text: '-- select field --' }]

        const options = [{ value: '', text: '-- select field --' }]

        for (const col of cachedColumns) {
          options.push({ value: col.uniqueTitle, text: col.title || col.uniqueTitle })
        }

        return options
      },
      async onModalOk (event) {

        const handled = await submitViewAi(this, event, { visibleKey: 'formEditVisible', tabIndex: 4 })

        if (!handled) return this.save()
      },
      async save () {

        console.log(`save form with form components!`)
        console.log(this.form.components)

        const saved = {
          title: this.title,
          uniqueTitle: this.uniqueTitle,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          components: this.form.components,
          submissionRules: { rules: this.submissionRules },
          defaultValueRules: { rules: this.defaultValueRules },
          formActions: { actions: this.formActions.map((action) => this.formActionForSave(action)) },
          filterByPageRecord: !!this.filterByPageRecord
        }

        if (this.showAuthenticatorOption) saved.twoFactor = !!this.twoFactor

        this.parentDesigner.saveActiveComponent(saved)
      }
    }
  }
</script>

<style>
.workflow-overlap-warning {
  color: #8a5a12;
  font-size: 0.85rem;
  margin: 0.35rem 0 0;
}
/* ── Form Editor: Row/Column Layout ── */
.form-row-block {
  border: 1px solid #2a2a28;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  background: rgba(30, 30, 27, 0.4);
}
.form-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a2a28;
}
.form-row-label {
  color: #9ad12b;
  font-size: 0.8em;
  font-weight: 500;
}
.form-col-label {
  color: #7a9a2b;
  font-size: 0.78em;
  font-weight: 500;
}
.form-row-columns {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.form-col-block {
  flex: 1;
  min-width: 180px;
  border: 1px solid #2a2a28;
  border-radius: 5px;
  padding: 10px;
  background: rgba(26, 26, 24, 0.5);
}
.form-edit-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #2a2a28;
}
.delete-inline {
  font-size: 11px;
  font-weight: bolder;
  color: #f82471;
  border: 1px solid #f82471;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 3px;
  transition: background 0.15s, color 0.15s;
}
.delete-inline:hover {
  background-color: #f82471;
  color: #fff;
}

/* ── Rules Button Group ── */
.rules-button-group {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.submission-rules-btn {
  color: #7a7a78;
  background: #1e1e1b;
  border: 1px dashed #3a3a38;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 0.82em;
  cursor: pointer;
  transition: all 0.15s;
}
.submission-rules-btn:hover {
  color: #9ad12b;
  background: #1e2e12;
  border-color: #9ad12b;
}
.submission-rules-btn.has-rules {
  color: #9ad12b;
  border-color: #9ad12b;
  border-style: solid;
  background: #1e2e12;
}
/* ── Form Actions Tab ── */
.section-description {
  color: #a8a8a4;
  font-size: 0.78em;
  margin-bottom: 12px;
}
.form-action-block {
  border: 1px solid #2a2a28;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;
  background: rgba(30, 30, 27, 0.4);
}
.form-action-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a2a28;
}
.form-action-label {
  color: #9ad12b;
  font-size: 0.8em;
  font-weight: 500;
}
.variable-mappings {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #2a2a28;
}
.variable-mappings-header {
  font-size: 0.75em;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}
.kv-variable-name {
  flex: 0.8;
  display: flex;
  align-items: center;
  gap: 6px;
}
.kv-variable-source {
  flex: 0.7;
}
.kv-variable-field {
  flex: 1;
}
.variable-tag {
  color: #9ad12b;
  font-family: monospace;
  font-size: 0.82em;
  background: rgba(154, 209, 43, 0.1);
  padding: 2px 6px;
  border-radius: 3px;
}
.variable-location {
  color: #4a4a47;
  font-size: 0.7em;
  font-family: monospace;
}
.no-variables-msg {
  color: #a8a8a4;
  font-size: 0.78em;
  font-style: italic;
  padding: 8px 0;
}
.kv-row {
  display: flex;
  gap: 8px;
  padding: 3px 0;
  align-items: center;
}
.kv-header-row {
  font-size: 0.7rem;
  font-weight: 600;
  color: #a8a8a4;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 6px;
  border-bottom: 1px solid #1e1e1b;
  margin-bottom: 4px;
}
.kv-input {
  background-color: #1a1a17 !important;
  color: #e8e8e6 !important;
  border-color: #2e2e2b !important;
  font-size: 0.8rem;
}

.submission-rules-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  font-size: 0.75em;
  font-weight: 700;
  background: #9ad12b;
  color: #141412;
  margin-left: 6px;
  padding: 0 4px;
}
</style>
